import { useAuthStore } from '@/state/store/auth';
import { useUserStore } from '@/store';

const BACKEND_URL =
  (import.meta.env.VITE_BACKEND_API_URL as string | undefined)?.replace(/\/$/, '') || '';

export class CrossAppSignInLinkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CrossAppSignInLinkError';
  }
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

export const LEGAL_TEMPLATE_MAIN_FEATURE_ID = 'legal-template';
export const LEGAL_TEMPLATE_DOCUMENT_GENERATION_FEATURE_ID =
  'legal-template-document-generation';

export interface SubscriptionUsage {
  FeatureId: string;
  MainFeatureId: string;
  Used: number;
  TotalLimit: number;
  IsLisenced: boolean;
  HasLicensing: boolean;
}

export interface ActiveSubscription {
  DataNature: string;
  Usage: SubscriptionUsage[];
  CustomPackageUsage: SubscriptionUsage[];
  IsPurchasedPlan: boolean;
}

export interface UpdateSubscriptionUsageInput {
  featureId: string;
  usage: number;
}

const extractSignInUrl = (payload: unknown): string | null => {
  if (typeof payload === 'string' && /^https?:\/\//i.test(payload)) return payload;
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const candidates = [record.Url, record.url, record.SignInLink, record.signInLink, record.Link, record.link];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && /^https?:\/\//i.test(candidate)) return candidate;
  }

  if (record.Data) return extractSignInUrl(record.Data);
  if (record.data) return extractSignInUrl(record.data);
  if (record.Result) return extractSignInUrl(record.Result);
  if (record.result) return extractSignInUrl(record.result);
  return null;
};

const subscriptionHeaders = (): Record<string, string> => {
  const { orgId, sigOrgId } = useAuthStore.getState();
  return {
    'Content-Type': 'application/json',
    ...(orgId ? { 'x-blocks-org-id': orgId } : {}),
    ...(sigOrgId ? { 'x-sig-org-id': sigOrgId } : {}),
  };
};

export const getActiveSubscription = async (): Promise<ActiveSubscription | null> => {
  if (!BACKEND_URL) {
    throw new CrossAppSignInLinkError('VITE_BACKEND_API_URL is not configured');
  }

  const response = await fetch(`${BACKEND_URL}/subscription/get-subscription`, {
    method: 'POST',
    credentials: 'include',
    headers: subscriptionHeaders(),
    body: JSON.stringify({ MainFeatureId: LEGAL_TEMPLATE_MAIN_FEATURE_ID }),
  });

  if (!response.ok) {
    throw new CrossAppSignInLinkError(
      `GetSubscription failed: ${response.status} ${response.statusText}`
    );
  }

  const payload: unknown = await response.json();
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const subscription = 'data' in record ? record.data : 'Data' in record ? record.Data : record;
  return subscription && typeof subscription === 'object'
    ? (subscription as ActiveSubscription)
    : null;
};

export const updateSubscriptionUsage = async (
  input: UpdateSubscriptionUsageInput
): Promise<unknown> => {
  if (!BACKEND_URL) {
    throw new CrossAppSignInLinkError('VITE_BACKEND_API_URL is not configured');
  }

  const response = await fetch(`${BACKEND_URL}/subscription/update-subscription-usage`, {
    method: 'POST',
    credentials: 'include',
    headers: subscriptionHeaders(),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new CrossAppSignInLinkError(
      `UpdateSubscriptionUsage failed: ${response.status} ${response.statusText}`
    );
  }

  const responseBody = await response.text();
  if (!responseBody) return null;

  try {
    return JSON.parse(responseBody);
  } catch {
    return responseBody;
  }
};

export const generateCrossAppSignInUrl = async (): Promise<string> => {
  if (!BACKEND_URL) {
    throw new CrossAppSignInLinkError('VITE_BACKEND_API_URL is not configured');
  }

  const { sigOrgId } = useAuthStore.getState();
  const { user } = useUserStore.getState();
  if (!sigOrgId) {
    throw new CrossAppSignInLinkError('MISSING_SIG_ORG');
  }

  const response = await fetch(`${BACKEND_URL}/subscription/view-subscriptions`, {
    method: 'POST',
    credentials: 'include',
    headers: subscriptionHeaders(),
    body: JSON.stringify({
      Email: user?.email ?? '',
      FirstName: user?.firstName ?? '',
      LastName: user?.lastName ?? '',
      OrganizationId: sigOrgId,
      PhoneNumber: user?.phoneNumber ?? '',
      Language: user?.language || 'en-US',
    }),
  });

  if (!response.ok) {
    throw new CrossAppSignInLinkError(
      `GenerateCrossAppSignInLink failed: ${response.status} ${response.statusText}`
    );
  }

  const envelope = (await response.json()) as Envelope<unknown>;
  const url = extractSignInUrl(envelope?.data) ?? extractSignInUrl(envelope);
  if (!url) {
    throw new CrossAppSignInLinkError('Cross-app sign-in response did not include a URL');
  }
  return url;
};
